// /pure: the default entry injects Stripe.js as a side effect of being
// imported, i.e. on every page of the bundle.
import { useEffect, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js/pure'

import { settings } from '../store/constants'
import { AnalyticsService } from '../services/analytics'
import { injectStripeJs, loadWithRetry } from './stripe-load'

// Stripe.js is ~400 KB and only the booking/checkout screens need it, so it
// is loaded on first use rather than at module load (which put it on every
// marketing page and cost the homepage ~0.3 s of main thread on mobile).
// Deferring below `<Elements>` is enough: the script is injected here, so
// nothing in the layout has to reference js.stripe.com.
//
// We inject the script ourselves (lib/stripe-load.js) and hand over to
// loadStripe only once window.Stripe exists: Stripe's own loader cannot be
// retried after a failure. One retry after 1.5 s; if that fails too the
// promise resolves null (never rejects, so no unhandled $exception), the
// failure is an event (`stripe_js_load_failed`), and the pay panels offer
// Stripe's hosted card page, which needs no Stripe.js (useStripeLoadFailed).
let stripePromise = null
let loadFailed = false
const listeners = new Set()

const giveUp = () => {
  loadFailed = true
  listeners.forEach(fn => { try { fn(true) } catch (e) { /* a gone component */ } })
  return null
}

export const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadWithRetry({
      load: () => injectStripeJs(),
      onAttemptFailed: (err, attempt, willRetry) => AnalyticsService.trackStripeJsLoadFailed(err?.message, attempt, willRetry)
    })
      .then(ok => (ok ? loadStripe(settings.stripeApiKey) : giveUp()))
      .catch(err => {
        AnalyticsService.trackStripeJsLoadFailed(err?.message, 0, false)
        return giveUp()
      })
  }
  return stripePromise
}

export const stripeLoadFailed = () => loadFailed

// true once Stripe.js has failed twice in this page.
export const useStripeLoadFailed = () => {
  const [failed, setFailed] = useState(loadFailed)
  useEffect(() => {
    if (loadFailed) setFailed(true)
    listeners.add(setFailed)
    return () => { listeners.delete(setFailed) }
  }, [])
  return failed
}
